import { useState } from "react";
import { Card } from "@/core/components/ui/card";
import {
  SimpleSidebar,
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@/core/components/ui/simple-sidebar/simple-sidebar";
import { cn } from "@/core/utils/utils";
import { CELL_FIGURE_QUESTION } from "@/payroll/ai-script/ai-script-explanation-chat/selection-context";
import { AiScriptExplanationChatSidebar } from "@/payroll/ai-script/ai-script-explanation-chat-sidebar";
import type { AiScriptChatSelectionContext } from "@/payroll/ai-script/types";
import { ReportWorkbook } from "@/profitability/report-workbook";
import type { EnrichedProfitabilityRun } from "@/profitability/types";
import { reportChatPath } from "@/profitability/utils";
import { RunContent } from "./run-content";
import { RunHeader } from "./run-header";
const CHAT_SURFACE = {
  title: "Profitability Chat",
  subtitle: "Chats about your profitability reports",
  composerPlaceholder: "Ask about this report, or any other period...",
  suggestedPrompts: [
    "Summarize this report",
    "Which technicians had the lowest gross profit percent?",
    "How does this compare to the week before?",
  ],
};
export const LoadedReport = ({
  businessId,
  run,
}: {
  businessId: string;
  run: EnrichedProfitabilityRun;
}) => {
  const [selectionContext, setSelectionContext] =
    useState<AiScriptChatSelectionContext | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { workbookReady } = run.display;

  const handleChatOpenChange = (open: boolean) => {
    setIsChatOpen(open);
    if (!open) {
      setSelectionContext(null);
      setPendingQuestion(null);
    }
  };

  const handleAskAiAboutCell = (context: AiScriptChatSelectionContext) => {
    setSelectionContext(context);
    setPendingQuestion(CELL_FIGURE_QUESTION);
    setIsChatOpen(true);
  };

  return (
    <SimpleSidebarLayout
      open={isChatOpen}
      onOpenChange={handleChatOpenChange}
      className="min-h-0 flex-1"
    >
      <SimpleSidebarContent removeScrollArea className="flex flex-col">
        <RunHeader
          businessId={businessId}
          run={run}
          onOpenChat={() => setIsChatOpen(true)}
        />
        <Card
          className={cn(
            "flex w-full flex-col overflow-hidden border-stroke-tertiary shadow-none",
            // Only the workbook earns the rest of the viewport. A status message
            // stretched to full height is just a large empty box.
            { "min-h-0 flex-1": workbookReady, "flex-none": !workbookReady },
          )}
        >
          <RunContent businessId={businessId} run={run}>
            <ReportWorkbook
              businessId={businessId}
              runId={run.id}
              onAskAiAboutCell={handleAskAiAboutCell}
            />
          </RunContent>
        </Card>
      </SimpleSidebarContent>

      <SimpleSidebar>
        {isChatOpen && (
          <AiScriptExplanationChatSidebar
            businessId={businessId}
            runId={run.id}
            buildChatPath={reportChatPath}
            selectionContext={selectionContext}
            onClearSelectionContext={() => setSelectionContext(null)}
            pendingQuestion={pendingQuestion}
            onQuestionPrepared={() => setPendingQuestion(null)}
            title={CHAT_SURFACE.title}
            subtitle={CHAT_SURFACE.subtitle}
            composerPlaceholder={CHAT_SURFACE.composerPlaceholder}
            suggestedPrompts={CHAT_SURFACE.suggestedPrompts}
            onClose={() => handleChatOpenChange(false)}
          />
        )}
      </SimpleSidebar>
    </SimpleSidebarLayout>
  );
};
